import type { CoreReadRouter } from "@bt/core";
import {
  type Distribution,
  type InstallationReport,
  PROCEDURE_AREAS_LIST,
  type ProcedureArea,
} from "@bt/domain/report";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { inferRouterInputs } from "@trpc/server";
import type { TFunction } from "i18next";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Segmented } from "@/components/ui/misc";
import { type BarItem, BarList } from "../charts/bar-list";
import { DistributionStrip } from "../charts/distribution-strip";
import { ChartFrame, type Column, type Row } from "../charts/frame";
import { Histogram } from "../charts/histogram";
import { type Series, TimeSeries } from "../charts/time-series";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { reportOf, useTRPC } from "../trpc";
import { useView } from "../view";

/**
 * The pieces every page of package 12 is built from (ADR 0016): sections, the latest report, and
 * framed figures with their table and CSV. They draw what core sends and compute nothing beyond
 * folding a long list into "Other".
 */

export type FigureName =
  inferRouterInputs<CoreReadRouter>["installation"]["history"]["figures"][number];

/** At most this many lines in a chart; the rest fold into "Other" (dataviz). */
const MAX_SERIES = 4;

/** An area of the app (procedures, interactions, demos) by its name; "other" for none. */
export const areaLabel = (t: TFunction) => (value: string) =>
  (PROCEDURE_AREAS_LIST as readonly string[]).includes(value)
    ? t(`adminApp.areas.${value as ProcedureArea}`)
    : value === "other"
      ? t("adminApp.kit.other")
      : value;

export function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** Two or three figures side by side. */
export function Grid({ children, columns = 2 }: { children: ReactNode; columns?: 2 | 3 }) {
  return (
    <div className={columns === 3 ? "grid grid-cols-3 gap-4" : "grid grid-cols-2 gap-4"}>
      {children}
    </div>
  );
}

/** The installation's latest report, or what stands in for it. */
export function ReportView({ children }: { children: (report: InstallationReport) => ReactNode }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { installation } = useView();
  const query = useQuery(trpc.installation.get.queryOptions({ name: installation }));
  return (
    <QueryView query={query}>
      {(data) => {
        const report = reportOf(data);
        return report ? (
          children(report)
        ) : (
          <Card className="p-4 text-sm text-muted-foreground">{t("adminApp.kit.noReport")}</Card>
        );
      }}
    </QueryView>
  );
}

/** A few headline numbers, each with what it is over. */
export function Stats({
  items,
}: {
  items: readonly { key: string; label: string; value: string; detail?: ReactNode }[];
}) {
  return (
    <div className="grid grid-cols-4 gap-3">
      {items.map((item) => (
        <Card key={item.key} className="p-3">
          <p className="text-sm text-muted-foreground">{item.label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight tabular">{item.value}</p>
          {item.detail ? <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p> : null}
        </Card>
      ))}
    </div>
  );
}

/** The first three lines and the rest summed as "Other" when there are more than four. */
function folded(lines: readonly Series[], other: string): Series[] {
  if (lines.length <= MAX_SERIES) return [...lines];
  const rest = lines.slice(MAX_SERIES - 1);
  const periods = rest[0]?.points.length ?? 0;
  return [
    ...lines.slice(0, MAX_SERIES - 1),
    {
      key: "other",
      label: other,
      points: Array.from({ length: periods }, (_, i) => {
        const values = rest.map((line) => line.points[i]).filter((v) => typeof v === "number");
        return values.length ? values.reduce((sum, v) => sum + (v ?? 0), 0) : null;
      }),
    },
  ];
}

/** Figures core follows over the range: stocks and flows as lines, one band behind the first. */
export function HistoryFigure({
  title,
  description,
  figures,
  csvName,
  formatValue,
  integer = true,
}: {
  title: string;
  description?: string;
  figures: readonly { figure: FigureName; label: string }[];
  csvName: string;
  formatValue?: (n: number) => string;
  integer?: boolean;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const { installation, range } = useView();
  const query = useQuery(
    trpc.installation.history.queryOptions({
      name: installation,
      range,
      figures: figures.map((f) => f.figure),
    }),
  );
  const write = formatValue ?? format.count;
  return (
    <QueryView query={query}>
      {(data) => {
        const labelOf = (figure: string) =>
          figures.find((f) => f.figure === figure)?.label ?? figure;
        const lines: Series[] = [];
        let band: { label: string; low: (number | null)[]; high: (number | null)[] } | undefined;
        for (const series of data.series) {
          if (series.kind === "band") {
            lines.push({
              key: series.figure,
              label: labelOf(series.figure),
              points: series.points.map((p) => p?.median ?? null),
            });
            band ??= {
              label: t("adminApp.kit.middleHalf"),
              low: series.points.map((p) => p?.p25 ?? null),
              high: series.points.map((p) => p?.p75 ?? null),
            };
          } else {
            lines.push({
              key: series.figure,
              label: labelOf(series.figure),
              points: series.points,
            });
          }
        }
        const columns: Column[] = [
          {
            key: "period",
            label: t("adminApp.health.period"),
            format: (v) => format.period(String(v)),
          },
          ...lines.map((line) => ({
            key: line.key,
            label: line.label,
            numeric: true,
            format: (v: unknown) => (typeof v === "number" ? write(v) : "–"),
          })),
        ];
        const rows: Row[] = data.periods.map((period, i) => ({
          period,
          ...Object.fromEntries(lines.map((line) => [line.key, line.points[i] ?? null])),
        }));
        return (
          <ChartFrame
            title={title}
            description={description}
            csvName={csvName}
            columns={columns}
            rows={rows}
          >
            <TimeSeries
              periods={data.periods}
              series={folded(lines, t("adminApp.kit.other"))}
              band={band}
              formatValue={write}
              formatPeriod={format.period}
              label={title}
              height={180}
              integer={integer}
            />
          </ChartFrame>
        );
      }}
    </QueryView>
  );
}

/**
 * A day count over the range by value (`usage.history`, `signals.history`): the largest values
 * as lines, the rest as "Other".
 */
export function CountsFigure({
  title,
  description,
  source,
  name,
  csvName,
  labelOf = (value) => value,
  formatValue,
}: {
  title: string;
  description?: string;
  source: "usage" | "signals";
  name: string;
  csvName: string;
  labelOf?: (value: string) => string;
  /** For counts that aren't counts, such as bytes. */
  formatValue?: (n: number) => string;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const write = formatValue ?? format.count;
  const { installation, range } = useView();
  const usage = useQuery({
    ...trpc.usage.history.queryOptions({ name: installation, range, metric: name }),
    enabled: source === "usage",
  });
  const signals = useQuery({
    ...trpc.signals.history.queryOptions({ name: installation, range, signal: name }),
    enabled: source === "signals",
  });
  const query = source === "usage" ? usage : signals;
  return (
    <QueryView query={query}>
      {(data) => {
        const shown = data.values.slice(
          0,
          data.values.length > MAX_SERIES ? MAX_SERIES - 1 : MAX_SERIES,
        );
        const rest = data.values.slice(shown.length);
        const lines: Series[] = shown.map((v) => ({
          key: v.value || "total",
          label: v.value ? labelOf(v.value) : title,
          points: v.counts,
        }));
        if (rest.length) {
          lines.push({
            key: "other",
            label: t("adminApp.kit.other"),
            points: data.periods.map((_, i) =>
              rest.reduce((sum, v) => sum + (v.counts[i] ?? 0), 0),
            ),
          });
        }
        const columns: Column[] = [
          {
            key: "period",
            label: t("adminApp.health.period"),
            format: (v) => format.period(String(v)),
          },
          ...data.values.map((v) => ({
            key: v.value || "total",
            label: v.value ? labelOf(v.value) : title,
            numeric: true,
            format: (n: unknown) => write(Number(n)),
          })),
        ];
        const rows: Row[] = data.periods.map((period, i) => ({
          period,
          ...Object.fromEntries(data.values.map((v) => [v.value || "total", v.counts[i] ?? 0])),
        }));
        return (
          <ChartFrame
            title={title}
            description={description}
            csvName={csvName}
            columns={columns}
            rows={rows}
            aside={
              <span className="text-sm text-muted-foreground tabular">
                {write(data.values.reduce((sum, v) => sum + v.total, 0))}
              </span>
            }
          >
            {data.values.length ? (
              <TimeSeries
                periods={data.periods}
                series={lines}
                formatValue={write}
                formatPeriod={format.period}
                label={title}
                height={160}
                integer={!formatValue}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
            )}
          </ChartFrame>
        );
      }}
    </QueryView>
  );
}

/**
 * Day counts of a few usage metrics over the range as one line each, summed over their values:
 * one measure against another, such as demo requests against demos opened.
 */
export function ComparedCounts({
  title,
  description,
  lines,
  csvName,
}: {
  title: string;
  description?: string;
  lines: readonly { metric: string; label: string }[];
  csvName: string;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const { installation, range } = useView();
  const queries = useQueries({
    queries: lines.map((line) =>
      trpc.usage.history.queryOptions({ name: installation, range, metric: line.metric }),
    ),
  });
  // The first query still loading or failed stands for all of them.
  const waiting = queries.find((q) => q.data === undefined);
  if (waiting) return <QueryView query={waiting}>{() => null}</QueryView>;
  const data = queries.map((q) => q.data);
  const first = data[0];
  if (!first) return null;
  const series: Series[] = lines.map((line, n) => ({
    key: line.metric,
    label: line.label,
    points: first.periods.map((_, i) =>
      (data[n]?.values ?? []).reduce((sum, v) => sum + (v.counts[i] ?? 0), 0),
    ),
  }));
  return (
    <ChartFrame
      title={title}
      description={description}
      csvName={csvName}
      columns={[
        {
          key: "period",
          label: t("adminApp.health.period"),
          format: (v) => format.period(String(v)),
        },
        ...series.map((s) => ({ key: s.key, label: s.label, numeric: true })),
      ]}
      rows={first.periods.map((period, i) => ({
        period,
        ...Object.fromEntries(series.map((s) => [s.key, s.points[i] ?? 0])),
      }))}
    >
      <TimeSeries
        periods={first.periods}
        series={series}
        formatValue={format.count}
        formatPeriod={format.period}
        label={title}
        height={160}
        integer
      />
    </ChartFrame>
  );
}

/** Counts per value, largest first, as bars with their numbers. */
export function TallyFigure({
  title,
  description,
  rows,
  csvName,
  labelOf = (value) => value,
  formatValue,
  limit,
  base,
}: {
  title: string;
  description?: string;
  rows: readonly { value: string; count: number }[] | undefined;
  csvName: string;
  labelOf?: (value: string) => ReactNode;
  formatValue?: (n: number) => string;
  /** Show this many, the rest in the table and the CSV. */
  limit?: number;
  /** What a share is out of, shown under each bar. */
  base?: number;
}) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const write = formatValue ?? format.count;
  const list = rows ?? [];
  const items: BarItem[] = list.slice(0, limit ?? list.length).map((row) => ({
    key: row.value,
    label: labelOf(row.value),
    value: row.count,
    detail: base
      ? format.share({
          part: row.count,
          base,
          percent: base >= 20 ? (row.count / base) * 100 : null,
        })
      : undefined,
  }));
  return (
    <ChartFrame
      title={title}
      description={description}
      csvName={csvName}
      columns={[
        { key: "value", label: t("adminApp.kit.value") },
        {
          key: "count",
          label: t("adminApp.kit.count"),
          numeric: true,
          format: (v) => write(Number(v)),
        },
      ]}
      rows={list.map((row) => ({ value: row.value, count: row.count }))}
    >
      <BarList items={items} formatValue={write} empty={t("adminApp.chart.noValues")} />
    </ChartFrame>
  );
}

/**
 * A distribution per garage: over the garages using it by default, switchable to all garages; the
 * strip with median, middle half, p90 and max, and the histogram on fixed edges.
 */
export function DistributionFigure({
  title,
  description,
  perGarage,
  csvName,
  formatValue,
}: {
  title: string;
  description?: string;
  perGarage: { all?: Distribution; using?: Distribution } | Distribution | undefined;
  csvName: string;
  formatValue?: (n: number) => string;
}) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const write = formatValue ?? format.decimal;
  const both = perGarage && "n" in perGarage ? null : perGarage;
  const [over, setOver] = useState<"using" | "all">("using");
  const shown: Distribution | undefined =
    perGarage && "n" in perGarage ? perGarage : (both?.[over] ?? both?.all);
  const stats = ["n", "none", "p25", "median", "p75", "p90", "max", "mean"] as const;
  return (
    <ChartFrame
      title={title}
      description={description}
      csvName={csvName}
      columns={[
        { key: "measure", label: t("adminApp.kit.measure") },
        { key: "value", label: t("adminApp.kit.value"), numeric: true },
      ]}
      rows={stats.map((s) => ({ measure: s, value: shown?.[s] ?? null }))}
      aside={
        both?.all && both.using ? (
          <Segmented<"using" | "all">
            aria-label={t("adminApp.kit.over")}
            value={over}
            onChange={setOver}
            options={[
              { value: "using", label: t("adminApp.kit.using") },
              { value: "all", label: t("adminApp.kit.allGarages") },
            ]}
          />
        ) : null
      }
    >
      {shown && shown.n > 0 ? (
        <div className="space-y-3">
          <DistributionStrip distribution={shown} format={write} />
          <Histogram
            counts={shown.histogram}
            formatCount={format.count}
            label={title}
            height={120}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
      )}
    </ChartFrame>
  );
}

/** Counts per bucket on edges of their own (vehicle age, mileage), with those of unknown value. */
export function BucketsFigure({
  title,
  description,
  counts,
  edges,
  formatEdge,
  continuous,
  unknown,
  csvName,
}: {
  title: string;
  description?: string;
  counts: readonly number[] | undefined;
  edges: readonly number[];
  formatEdge: (n: number) => string;
  /** Time or distance rather than a count (`Histogram`). */
  continuous?: boolean;
  unknown?: number;
  csvName: string;
}) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const values = counts ?? [];
  return (
    <ChartFrame
      title={title}
      description={description}
      csvName={csvName}
      columns={[
        { key: "from", label: t("adminApp.kit.from"), numeric: true },
        { key: "count", label: t("adminApp.kit.count"), numeric: true },
      ]}
      rows={[
        ...edges.map((edge, i) => ({ from: edge, count: values[i] ?? 0 })),
        ...(unknown === undefined ? [] : [{ from: null, count: unknown }]),
      ]}
      aside={
        unknown ? (
          <span className="text-xs text-muted-foreground">
            {t("adminApp.kit.unknown", { count: unknown })}
          </span>
        ) : null
      }
    >
      {values.some((v) => v > 0) ? (
        <Histogram
          counts={values}
          edges={edges}
          formatCount={format.count}
          formatEdge={formatEdge}
          continuous={continuous}
          label={title}
          height={140}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
      )}
    </ChartFrame>
  );
}

/**
 * A table sorted by a column the reader picks (the header buttons), showing the first `limit`
 * rows with "Show all"; the CSV always has every row.
 */
export function SortableTable({
  title,
  description,
  columns,
  rows,
  csvName,
  initialSort,
  limit = 10,
  render,
}: {
  title: string;
  description?: string;
  columns: readonly Column[];
  rows: readonly Row[];
  csvName: string;
  initialSort: string;
  limit?: number;
  /** A cell's content, where it is more than its formatted value (a flag, a link). */
  render?: (key: string, row: Row) => ReactNode;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(initialSort);
  const [descending, setDescending] = useState(true);
  const [all, setAll] = useState(false);
  const format = useAdminFormat();
  const sorted = [...rows].sort((a, b) => {
    const x = a[sort];
    const y = b[sort];
    const order =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x ?? "").localeCompare(String(y ?? ""));
    return descending ? -order : order;
  });
  const shown = all ? sorted : sorted.slice(0, limit);
  // A number without its own format is a count.
  const cell = (column: Column, value: Row[string] | undefined) =>
    column.format
      ? column.format(value ?? null)
      : typeof value === "number"
        ? format.count(value)
        : (value ?? "–");
  return (
    <ChartFrame
      title={title}
      description={description}
      csvName={csvName}
      columns={columns}
      rows={sorted}
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
      ) : (
        <div className="max-h-[32rem] scrollbar-thin overflow-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">{title}</caption>
            <thead className="sticky top-0 bg-surface">
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      sort === column.key ? (descending ? "descending" : "ascending") : "none"
                    }
                    className={column.numeric ? "px-2 py-1.5 text-right" : "px-2 py-1.5"}
                  >
                    <button
                      type="button"
                      className="font-medium whitespace-nowrap hover:text-foreground"
                      onClick={() => {
                        if (sort === column.key) setDescending((d) => !d);
                        else {
                          setSort(column.key);
                          setDescending(Boolean(column.numeric));
                        }
                      }}
                    >
                      {column.label}
                      {sort === column.key ? (descending ? " ↓" : " ↑") : ""}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: sorted rows have no identity of their own
                <tr key={i} className="border-b border-border/60 last:border-0">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={
                        column.numeric
                          ? "px-2 py-1.5 text-right whitespace-nowrap tabular"
                          : "px-2 py-1.5 whitespace-nowrap"
                      }
                    >
                      {render?.(column.key, row) ?? cell(column, row[column.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > limit ? (
        <button
          type="button"
          className="mt-2 text-sm font-medium text-brand-fg hover:underline"
          onClick={() => setAll((v) => !v)}
        >
          {all
            ? t("adminApp.kit.showFewer", { count: limit })
            : t("adminApp.kit.showAll", { count: rows.length })}
        </button>
      ) : null}
    </ChartFrame>
  );
}
